// src/utils/storageUploads.js
import exifr from "exifr";
import { ref as storageRef, uploadBytesResumable, getDownloadURL, deleteObject } from "firebase/storage";
import { doc, setDoc, addDoc, collection, getDoc, serverTimestamp } from "firebase/firestore";
import { storage, db } from "../firebase";

/**
 * Client-side helper: create a JPEG thumbnail Blob from an image File/Blob.
 * - maxDim: maximum width or height of the thumbnail in pixels (keeps aspect).
 * - quality: jpeg quality 0..1
 */
async function createImageThumbnail(file, { maxDim = 400, quality = 0.75 } = {}) {
    // Only handle images
    if (!file || !(file.type || "").startsWith("image")) return null;

    // Use createImageBitmap when available for better performance
    let imgBitmap;
    try {
        imgBitmap = await createImageBitmap(file);
    } catch (e) {
        // fallback to HTMLImageElement
        const url = URL.createObjectURL(file);
        const img = await new Promise((resolve, reject) => {
            const i = new Image();
            i.onload = () => resolve(i);
            i.onerror = reject;
            i.src = url;
        });
        // draw image element to canvas below
        imgBitmap = null;
        URL.revokeObjectURL(url);

        const w = img.width;
        const h = img.height;
        const scale = Math.min(1, maxDim / Math.max(w, h));
        const cw = Math.round(w * scale);
        const ch = Math.round(h * scale);

        const canvas = document.createElement("canvas");
        canvas.width = cw;
        canvas.height = ch;
        const ctx = canvas.getContext("2d");
        ctx.drawImage(img, 0, 0, cw, ch);

        const blob = await new Promise((res) => canvas.toBlob(res, "image/jpeg", quality));
        return blob;
    }

    // if we have an ImageBitmap
    const w = imgBitmap.width;
    const h = imgBitmap.height;
    const scale = Math.min(1, maxDim / Math.max(w, h));
    const cw = Math.round(w * scale);
    const ch = Math.round(h * scale);

    const canvas = document.createElement("canvas");
    canvas.width = cw;
    canvas.height = ch;
    const ctx = canvas.getContext("2d");
    ctx.drawImage(imgBitmap, 0, 0, cw, ch);

    const blob = await new Promise((res) => canvas.toBlob(res, "image/jpeg", quality));
    try { imgBitmap.close && imgBitmap.close(); } catch (e) { }
    return blob;
}

/**
 * Upload one file to Firebase Storage and persist metadata to Firestore
 * under trips/{tripId}/media/{mediaId}.
 *
 * Improvements:
 * - Uploads a thumbnail for images and persists thumbnailUrl.
 * - Sets cacheControl metadata to encourage long caching of immutable objects.
 * - Persists both fullUrl and thumbnailUrl in Firestore to avoid getDownloadURL usage later.
 */
export async function uploadFileAndSaveMeta(file, tripId, user, onProgress) {
    if (!user || !user.uid) throw new Error("User must be authenticated");
    if (!tripId) throw new Error("tripId required");
    if (!storage) throw new Error("Firebase Storage not initialized");
    if (!db) throw new Error("Firestore not initialized");

    // 1) Attempt EXIF extraction (best-effort)
    let captureDate = null;
    let gps = null;
    try {
        if (file.type && file.type.startsWith("image")) {
            const exif = await exifr.parse(file, { tiff: true, exif: true, gps: true });
            if (exif) {
                if (exif.DateTimeOriginal) {
                    captureDate = exif.DateTimeOriginal instanceof Date ? exif.DateTimeOriginal.toISOString() : String(exif.DateTimeOriginal);
                } else if (exif.CreateDate) {
                    captureDate = exif.CreateDate instanceof Date ? exif.CreateDate.toISOString() : String(exif.CreateDate);
                }
                if (typeof exif.latitude === "number" && typeof exif.longitude === "number") {
                    gps = { lat: exif.latitude, lng: exif.longitude };
                } else if (exif.gps && exif.gps.latitude && exif.gps.longitude) {
                    gps = { lat: exif.gps.latitude, lng: exif.gps.longitude };
                }
            }
        }
    } catch (exifErr) {
        console.warn("exifr parse failed (continuing):", exifErr);
    }

    // 2) Prepare storage paths & metadata
    const safeName = (file.name || `file-${Date.now()}`).replace(/\s+/g, "_");
    const timestamp = Date.now();
    const basePath = `${user.uid}/trips/${tripId}/${timestamp}_${safeName}`;
    const storagePath = basePath; // main object path
    const thumbPath = `${basePath}.thumb.jpg`; // thumbnail path

    // Set metadata with long cache control for immutable content.
    // Note: if you expect overwrites, use shorter TTLs or different strategy.
    const cacheControlImmutable = "public, max-age=31536000, immutable";

    // 3) Upload main file
    const mainRef = storageRef(storage, storagePath);
    // include contentType and cacheControl
    const mainMeta = { contentType: file.type || "application/octet-stream", cacheControl: cacheControlImmutable };

    const mainUploadTask = uploadBytesResumable(mainRef, file, mainMeta);

    const mainSnapshot = await new Promise((resolve, reject) => {
        mainUploadTask.on(
            "state_changed",
            (snap) => {
                const pct = snap.totalBytes ? Math.round((snap.bytesTransferred / snap.totalBytes) * 100) : 0;
                if (typeof onProgress === "function") onProgress(pct);
            },
            (err) => reject(err),
            () => resolve(mainUploadTask.snapshot)
        );
    });

    // 4) For images: create & upload thumbnail (best-effort)
    let thumbnailUrl = null;
    try {
        if (file.type && file.type.startsWith("image")) {
            const thumbBlob = await createImageThumbnail(file, { maxDim: 400, quality: 0.7 });
            if (thumbBlob) {
                const thumbRef = storageRef(storage, thumbPath);
                // set cache-control on thumbnail too
                const thumbMeta = { contentType: "image/jpeg", cacheControl: cacheControlImmutable };
                // upload thumbnail
                await uploadBytesResumable(thumbRef, thumbBlob, thumbMeta).then(() => { });
                // get thumbnail url (one-time)
                thumbnailUrl = await getDownloadURL(thumbRef);
            }
        }
    } catch (thumbErr) {
        console.warn("Thumbnail creation/upload failed (continuing):", thumbErr);
        thumbnailUrl = null;
    }

    // 5) getDownloadURL for main file (one-time)
    let fullUrl = null;
    try {
        fullUrl = await getDownloadURL(mainSnapshot.ref);
    } catch (urlErr) {
        // might be permission issues — leave null and persist storagePath so server-side/authorized workers can resolve later
        console.warn("getDownloadURL failed (still persisting metadata):", urlErr);
        fullUrl = null;
    }

    // 6) Compose media metadata to persist
    const mediaId = `${timestamp}-${Math.random().toString(36).slice(2, 8)}`;
    const clientCreatedAt = new Date().toISOString();
    // after uploading main file and attempting thumbnail upload
    const mediaDoc = {
        id: mediaId,
        name: file.name || safeName,
        mime: file.type || "application/octet-stream",
        size: file.size || 0,
        type: file.type && file.type.startsWith("video") ? "video" : "image",
        storagePath,                // original file storage path
        fullUrl: fullUrl || null,   // one-time resolved full URL (may be null)
        url: fullUrl || null,       // keep backward compatibility
        thumbnailPath: thumbnailUrl ? thumbPath : null, // store storage path for thumbnail (so we can delete later)
        thumbnailUrl: thumbnailUrl || null,             // download URL for grid
        date: captureDate || clientCreatedAt,
        gps: gps || null,
        uploadedBy: user.uid,
        clientCreatedAt,
    };


    // 7) Persist to Firestore with retries (same policy as before)
    const mediaDocRef = doc(db, "trips", tripId, "media", mediaId);
    const maxRetries = 2;
    let attempt = 0;
    let lastError = null;
    let persistedDocId = null;

    while (attempt <= maxRetries) {
        try {
            attempt++;
            await setDoc(mediaDocRef, { ...mediaDoc, uploadedAt: serverTimestamp() });
            persistedDocId = mediaId;
            lastError = null;
            break;
        } catch (err) {
            lastError = err;
            const isPermission = err && (String(err.code || "").toLowerCase().includes("permission") || String(err.message || "").toLowerCase().includes("permission") || String(err.message || "").toLowerCase().includes("auth"));
            if (isPermission) {
                try {
                    const collRef = collection(db, "trips", tripId, "media");
                    const addedRef = await addDoc(collRef, { ...mediaDoc, uploadedAt: serverTimestamp() });
                    persistedDocId = addedRef.id;
                    lastError = null;
                    break;
                } catch (addErr) {
                    lastError = addErr;
                    if (attempt > maxRetries) break;
                }
            }
            const backoffMs = 200 * Math.pow(2, attempt);
            await new Promise((r) => setTimeout(r, backoffMs));
        }
    }

    // 8) If persistence failed, attempt cleanup (best-effort)
    if (!persistedDocId) {
        try {
            await deleteObject(mainRef).catch((err) => {
                console.warn("Failed to delete uploaded storage object after Firestore persist failure:", err);
            });
            // also try to delete thumbnail if uploaded
            if (thumbnailUrl) {
                try {
                    const thumbRef = storageRef(storage, thumbPath);
                    await deleteObject(thumbRef).catch(() => { });
                } catch (e) { }
            }
        } catch (e) { /* ignore */ }

        return {
            ...mediaDoc,
            uploadedAt: new Date().toISOString(),
            _firestorePersisted: false,
            _firestoreError: lastError ? String(lastError.message || lastError) : "unknown",
        };
    }

    // 9) Read the saved doc to obtain server timestamp if available and return result
    try {
        const persistedRef = doc(db, "trips", tripId, "media", persistedDocId);
        const persistedSnap = await getDoc(persistedRef);
        const savedData = persistedSnap.exists() ? persistedSnap.data() : null;
        let uploadedAtISO = null;
        try {
            const uploadedAtTs = savedData?.uploadedAt;
            uploadedAtISO = uploadedAtTs && typeof uploadedAtTs.toDate === "function" ? uploadedAtTs.toDate().toISOString() : (uploadedAtTs || null);
        } catch (e) {
            uploadedAtISO = null;
        }

        return {
            id: persistedDocId,
            ...mediaDoc,
            uploadedAt: uploadedAtISO || new Date().toISOString(),
            _firestorePersisted: true,
        };
    } catch (finalReadErr) {
        return {
            id: persistedDocId,
            ...mediaDoc,
            uploadedAt: new Date().toISOString(),
            _firestorePersisted: true,
            _firestoreReadError: String(finalReadErr.message || finalReadErr),
        };
    }
}
